import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.css';

const API_BASE = process.env.REACT_APP_API_URL || '/backend/api';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [contactInquiries, setContactInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [allInquiries, setAllInquiries] = useState([]);
  const [stats, setStats] = useState({
    totalContact: 0,
    totalBooking: 0
  });
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Check login status
  useEffect(() => {
    if (sessionStorage.getItem('isLoggedIn') !== 'true') {
      navigate('/login');
    } else {
      setUsername(sessionStorage.getItem('username') || 'Admin User');
      fetchAllData();
    }
  }, [navigate]);

  // Fetch all data from different sources
  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching data from API...');

      // Fetch contact inquiries from tbl_contact_inquiry (excluding bookings)
      try {
        const contactRes = await fetch(`${API_BASE}/query`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            query: "SELECT tci_id as id, tci_name as name, tci_email as email, tci_mobile as phone, tci_subject as subject, tci_message as message, tci_created_at as created_at FROM tbl_contact_inquiry WHERE tci_subject NOT LIKE 'Booking%' ORDER BY tci_id DESC LIMIT 100" 
          })
        });
        const contactData = await contactRes.json();
        if (contactData && Array.isArray(contactData.data)) {
          setContactInquiries(contactData.data);
          setStats(prev => ({ ...prev, totalContact: contactData.data.length }));
        } else {
          setContactInquiries([]);
          setStats(prev => ({ ...prev, totalContact: 0 }));
        }
      } catch (contactErr) {
        console.error('Error fetching contact inquiries:', contactErr);
        setContactInquiries([]);
      }

      // Fetch bookings (treated as inquiries with subject containing 'Booking')
      try {
        const bookingRes = await fetch(`${API_BASE}/query`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            query: "SELECT tci_id as id, tci_name as name, tci_email as email, tci_mobile as phone, tci_subject as subject, tci_message as message, tci_created_at as created_at FROM tbl_contact_inquiry WHERE tci_subject LIKE 'Booking%' ORDER BY tci_id DESC LIMIT 100" 
          })
        });
        const bookingData = await bookingRes.json();
        if (bookingData && Array.isArray(bookingData.data)) {
          setBookings(bookingData.data);
          setStats(prev => ({ ...prev, totalBooking: bookingData.data.length }));
        } else {
          setBookings([]);
          setStats(prev => ({ ...prev, totalBooking: 0 }));
        }
      } catch (bookingErr) {
        console.error('Error fetching bookings:', bookingErr);
        setBookings([]);
      }

      // Combine all inquiries into one list
      const allCombined = [];
      
      
      // Add contact inquiries with type (map tbl_contact_inquiry fields)
      contactInquiries.forEach(item => {
        allCombined.push({ 
          id: item.id || item.tci_id,
          name: item.name || item.tci_name,
          email: item.email || item.tci_email,
          phone: item.phone || item.tci_mobile,
          subject: item.subject || item.tci_subject,
          message: item.message || item.tci_message,
          created_at: item.created_at || item.tci_created_at,
          type: 'Contact', 
          typeColor: '#28a745' 
        });
      });
      
      // Add bookings with type
      bookings.forEach(item => {
        allCombined.push({ 
          id: item.id || item.tci_id,
          name: item.name || item.tci_name,
          email: item.email || item.tci_email,
          phone: item.phone || item.tci_mobile,
          subject: item.subject || item.tci_subject,
          message: item.message || item.tci_message,
          created_at: item.created_at || item.tci_created_at,
          type: 'Booking', 
          typeColor: '#007bff' 
        });
      });
      
      // Sort by date (newest first)
      allCombined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setAllInquiries(allCombined);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  // View inquiry details
  const viewContactInquiry = async (id) => {
    try {
      const response = await fetch(`${API_BASE}/contact-inquiry/${id}`);
      const data = await response.json();
      
      if (data.status) {
        const inquiry = data.data;
        alert(`Car Inquiry Details:\n\nName: ${inquiry.name}\nEmail: ${inquiry.email}\nPhone: ${inquiry.phone}\nCar: ${inquiry.car_model}\nMessage: ${inquiry.message}\nDate: ${new Date(inquiry.created_at).toLocaleDateString()}\nStatus: ${inquiry.status}`);
        
        if (inquiry.status === 'new') {
          await updateStatus(id, 'pending');
        }
      }
    } catch (err) {
      console.error('Error viewing inquiry:', err);
    }
  };

  // Update inquiry status
  const updateStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/contact-inquiry/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      const data = await response.json();
      if (data.status) {
        setContactInquiries(prev => 
          prev.map(item => 
            item.id === id ? { ...item, status: newStatus } : item
          )
        );
        fetchAllData(); // Refresh stats
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  // Delete inquiry
  const deleteInquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;

    try {
      const response = await fetch(`${API_BASE}/contact-inquiry/${id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.status) {
        setContactInquiries(prev => prev.filter(item => item.id !== id));
        fetchAllData();
      } else {
        alert('Failed to delete inquiry');
      }
    } catch (err) {
      alert('Error connecting to server');
    }
  };

  // Logout
  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/login');
  };

  // Get status badge class
  const getStatusClass = (status) => {
    switch (status) {
      case 'new': return 'new';
      case 'pending': return 'pending';
      case 'completed': return 'completed';
      default: return '';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Render car inquiries table
  const renderCarInquiriesTable = () => {
    if (loading) {
      return (
        <div className="inquiry-empty">
          <p>Loading...</p>
        </div>
      );
    }

    if ([].length === 0) {
      return (
        <div className="inquiry-empty">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 18H4V8L8 12L12 8L16 12L20 8V18Z" fill="#888"/>
          </svg>
          <p>No car inquiries yet</p>
          <p style={{ fontSize: '12px', color: '#aaa' }}>When customers submit inquiries from car detail pages, they will appear here.</p>
        </div>
      );
    }

    return (
      <table className="inquiry-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Car Model</th>
            <th>Message</th>
            <th>Date</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {[].map(inquiry => (
            <tr key={inquiry.id}>
              <td>#{inquiry.id}</td>
              <td><strong>{inquiry.name}</strong></td>
              <td><a href={`mailto:${inquiry.email}`}>{inquiry.email}</a></td>
              <td>{inquiry.phone}</td>
              <td>{inquiry.car_model}</td>
              <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {inquiry.message}
              </td>
              <td>{formatDate(inquiry.created_at)}</td>
              <td>
                <span className={`inquiry-status ${getStatusClass(inquiry.status)}`}>
                  {inquiry.status?.charAt(0).toUpperCase() + inquiry.status?.slice(1)}
                </span>
              </td>
              <td>
                <div className="inquiry-action">
                  <button className="view-btn" onClick={() => viewContactInquiry(inquiry.id)}>View</button>
                  <button className="delete-btn" onClick={() => deleteInquiry(inquiry.id)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Render contact inquiries table
  const renderContactInquiriesTable = () => {
    if (loading) {
      return (
        <div className="inquiry-empty">
          <p>Loading...</p>
        </div>
      );
    }

    if (contactInquiries.length === 0) {
      return (
        <div className="inquiry-empty">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 18H4V8L8 12L12 8L16 12L20 8V18Z" fill="#888"/>
          </svg>
          <p>No contact inquiries yet</p>
          <p style={{ fontSize: '12px', color: '#aaa' }}>When customers submit contact form, inquiries will appear here.</p>
        </div>
      );
    }

    return (
      <table className="inquiry-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Subject</th>
            <th>Message</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {contactInquiries.map(inquiry => (
            <tr key={inquiry.id}>
              <td>#{inquiry.id}</td>
              <td><strong>{inquiry.name}</strong></td>
              <td><a href={`mailto:${inquiry.email}`}>{inquiry.email}</a></td>
              <td>{inquiry.phone}</td>
              <td>{inquiry.subject}</td>
              <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {inquiry.message}
              </td>
              <td>{formatDate(inquiry.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Render bookings table
  const renderBookingsTable = () => {
    if (loading) {
      return (
        <div className="inquiry-empty">
          <p>Loading...</p>
        </div>
      );
    }

    if (bookings.length === 0) {
      return (
        <div className="inquiry-empty">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 18H4V8L8 12L12 8L16 12L20 8V18Z" fill="#888"/>
          </svg>
          <p>No bookings yet</p>
          <p style={{ fontSize: '12px', color: '#aaa' }}>When customers submit booking form, entries will appear here.</p>
        </div>
      );
    }

    return (
      <table className="inquiry-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Booking Details</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map(booking => (
            <tr key={booking.id}>
              <td>#{booking.id}</td>
              <td><strong>{booking.name}</strong></td>
              <td><a href={`mailto:${booking.email}`}>{booking.email}</a></td>
              <td>{booking.phone}</td>
              <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {booking.message}
              </td>
              <td>{formatDate(booking.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Render all inquiries combined table
  const renderAllInquiriesTable = () => {
    if (loading) {
      return (
        <div className="inquiry-empty">
          <p>Loading...</p>
        </div>
      );
    }

    if (allInquiries.length === 0) {
      return (
        <div className="inquiry-empty">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 18H4V8L8 12L12 8L16 12L20 8V18Z" fill="#888"/>
          </svg>
          <p>No inquiries yet</p>
          <p style={{ fontSize: '12px', color: '#aaa' }}>When customers submit any form, inquiries will appear here.</p>
        </div>
      );
    }

    return (
      <table className="inquiry-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Type</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Details</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {allInquiries.map(inquiry => (
            <tr key={`${inquiry.type}-${inquiry.id}`}>
              <td>#{inquiry.id}</td>
              <td>
                <span style={{ 
                  padding: '4px 12px', 
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: inquiry.typeColor + '20',
                  color: inquiry.typeColor
                }}>
                  {inquiry.type}
                </span>
              </td>
              <td><strong>{inquiry.name}</strong></td>
              <td><a href={`mailto:${inquiry.email}`}>{inquiry.email}</a></td>
              <td>{inquiry.phone}</td>
              <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {inquiry.car_model || inquiry.subject || inquiry.message}
              </td>
              <td>{formatDate(inquiry.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="dashboard-page">
      {/* Sidebar */}
      <div className="sidebar-dashboard">
        <div className="db-logo">
          <a href="/"><img src="/assets/images/logo/logo.png" alt="Logo" /></a>
        </div>
        <div className="db-menu">
          <ul>
            <li className="active">
              <a href="/dashboard">
                <div className="icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.5 8.83333H7.16667V0.5H0.5V8.83333ZM0.5 15.5H7.16667V10.5H0.5V15.5ZM8.83333 15.5H15.5V7.16667H8.83333V15.5ZM8.83333 0.5V5.5H15.5V0.5H8.83333Z" fill="#edb808" />
                  </svg>
                </div>
                <p>Dashboard</p>
              </a>
            </li>
            <li className="active-menu">
              <a href="/dashboard">
                <div className="icon">
                  <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.0013 15.7917L7.79297 14.6917C3.5013 10.8 0.667969 8.23333 0.667969 5.08333C0.667969 2.51667 2.68464 0.5 5.2513 0.5C6.7013 0.5 8.09297 1.175 9.0013 2.24167C9.90964 1.175 11.3013 0.5 12.7513 0.5C15.318 0.5 17.3346 2.51667 17.3346 5.08333C17.3346 8.23333 14.5013 10.8 10.2096 14.7L9.0013 15.7917Z" fill="#edb808" />
                  </svg>
                </div>
                <p>Inquiries</p>
              </a>
            </li>
          </ul>
        </div>
        <div className="db-user-info">
          <div className="user-name">{username}</div>
          <div className="user-role">Administrator</div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-dashboard-content">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-logo">
            <a href="/"><img src="/assets/images/logo/logo2.png" alt="Logo" /></a>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 12.5H11.5V14H4.5V2H11.5V3.5H10V5.5H6V11.5H10V12.5ZM4 13L6.5 10.5H8V9.5H6.5V8.5H8V7.5H6.5L4 5H2V13H4Z" fill="currentColor"/>
            </svg>
            Logout
          </button>
        </header>

        {/* Dashboard Content */}
        <div className="dashboard-content">
          <h2 className="title-dashboard">Dashboard</h2>

          {/* Stats Overview */}
          <div className="dashboard-overview">
            <div className="db-cards-wrapper">
              <div className="db-card-wrapper">
                <div className="db-card" onClick={() => setActiveTab('contact')} style={{ cursor: 'pointer' }}>
                  <div className="icon-overview">
                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.957 26C12.5364 26 12.1953 25.659 12.1953 25.2383C12.1953 24.8176 12.5364 24.4766 12.957 24.4766C19.2852 24.4766 24.4336 19.3282 24.4336 13C24.4336 6.67179 19.2852 1.52344 12.957 1.52344C12.5364 1.52344 12.1953 1.18239 12.1953 0.761719C12.1953 0.341047 12.5364 0 12.957 0C16.4295 0 19.694 1.35225 22.1495 3.80758C24.6048 6.26295 25.957 9.52758 25.957 13C25.957 16.4724 24.6048 19.737 22.1495 22.1924C19.694 24.6477 16.4295 26 12.957 26Z" fill="#edb808"/>
                    </svg>
                  </div>
                  <div className="content-overview">
                    <span>Contact Inquiries</span>
                    <div className="listing-text number">{stats.totalContact}</div>
                  </div>
                </div>
              </div>
              <div className="db-card-wrapper">
                <div className="db-card" onClick={() => setActiveTab('booking')} style={{ cursor: 'pointer' }}>
                  <div className="icon-overview">
                    <svg width="27" height="26" viewBox="0 0 27 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M24.5027 3.19109C23.1578 1.74559 21.2734 0.923122 19.2991 0.920073C17.3231 0.92236 15.4367 1.74439 14.0898 3.19016L13.6308 3.67522L13.1718 3.19016C10.4992 0.313704 6.00069 0.148513 3.12429 2.82112C2.99683 2.9396 2.87373 3.06265 2.75525 3.19016C-0.0798765 6.2482 -0.0798765 10.9743 2.75525 14.0323L12.9562 24.7899C13.3091 25.1624 13.8973 25.1784 14.2698 24.8255C14.282 24.8139 14.2939 24.8021 14.3055 24.7899L24.5027 14.0323C27.3376 10.9746 27.3376 6.2488 24.5027 3.19109Z" fill="#edb808"/>
                    </svg>
                  </div>
                  <div className="content-overview">
                    <span>Bookings</span>
                    <div className="listing-text number">{stats.totalBooking}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs for different inquiry types */}
          <div className="inquiry-section">
            <div className="inquiry-header">
              <div style={{ display: 'flex', gap: '10px', flexDirection: 'row', }}>
                <button 
                  className={`button_main_inner ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all')}
                  style={{ 
                    background: activeTab === 'all' ? '#edb808' : '#f8f9fa',
                    color: activeTab === 'all' ? '#fff' : '#333',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  All ({allInquiries.length})
                </button>
                {/* <button 
                  className={`button_main_inner ${activeTab === 'car' ? 'active' : ''}`}
                  onClick={() => setActiveTab('car')}
                  style={{ 
                    background: activeTab === 'car' ? '#edb808' : '#f8f9fa',
                    color: activeTab === 'car' ? '#fff' : '#333',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Car ({[].length})
                </button> */}
                <button 
                  className={`button_main_inner ${activeTab === 'contact' ? 'active' : ''}`}
                  onClick={() => setActiveTab('contact')}
                  style={{ 
                    background: activeTab === 'contact' ? '#edb808' : '#f8f9fa',
                    color: activeTab === 'contact' ? '#fff' : '#333',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Contact ({contactInquiries.length})
                </button>
                <button 
                  className={`button_main_inner ${activeTab === 'booking' ? 'active' : ''}`}
                  onClick={() => setActiveTab('booking')}
                  style={{ 
                    background: activeTab === 'booking' ? '#edb808' : '#f8f9fa',
                    color: activeTab === 'booking' ? '#fff' : '#333',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Booking ({bookings.length})
                </button>
              </div>
              <button 
                onClick={fetchAllData}
                style={{
                  background: '#edb808',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Refresh
              </button>
            </div>
            
            {error && (
              <div style={{ 
                padding: '15px', 
                marginBottom: '20px', 
                borderRadius: '5px', 
                background: '#f8d7da',
                color: '#721c24'
              }}>
                {error}
              </div>
            )}

            <div className="inquiry-table-container">
              {activeTab === 'all' && renderAllInquiriesTable()}
              {activeTab === 'contact' && renderContactInquiriesTable()}
              {activeTab === 'booking' && renderBookingsTable()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
